import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useEntityRegistered } from '@/admin/hooks/use-entity-registration'
import type { PaginatedResponse } from '@/shared/types/api'

// Entity keys that gate the pricing surfaces. Presence of the materialized
// EntityType row is the only gate (no feature flag, no tenant slug) - it
// mirrors the backend RequiresEntity() gate, so the two never drift.
export const MATERIAL_PRICING_ENTITY = 'material_pricing_rate'
export const LABOR_RATE_ENTITY = 'labor_rate'

export interface MaterialPricingRate {
  uuid: string
  geozone: number
  geozone_uuid: string
  geozone_name: string
  geozone_code: string
  product_line: string
  product_base: string
  material_code: string
  sap_short_code: string
  uom: string
  price: string
  source: string
  effective_date: string
  expires_date: string | null
  is_active: boolean
}

export interface LaborRate {
  uuid: string
  geozone: number
  geozone_uuid: string
  geozone_name: string
  geozone_code: string
  work_type: string
  product_line: string
  rate_per_square: string
  // ISO 4217 code the rate is stored in (WPS-161). Blank falls back to the
  // org's default currency on the read side.
  currency: string
  note: string
  effective_date: string
  expires_date: string | null
  is_active: boolean
}

export interface MaterialPricingRateInput {
  geozone_uuid: string
  product_line: string
  product_base?: string
  material_code?: string
  sap_short_code?: string
  uom?: string
  price: string
  source?: string
  effective_date: string
  expires_date?: string | null
  is_active?: boolean
}

export interface LaborRateInput {
  geozone_uuid: string
  work_type: string
  product_line: string
  rate_per_square: string
  // Optional on write; blank keeps the org-default fallback (WPS-161).
  currency?: string
  note?: string
  effective_date: string
  expires_date?: string | null
  is_active?: boolean
}

export interface PricingOption {
  value: string
  label: string
}

export interface PricingFacets {
  product_lines: string[]
  product_bases?: string[]
  effective_dates: string[]
  work_types?: PricingOption[]
  // Form dropdown option lists, sourced from the tenant's product templates /
  // product bases / settlement UOM attribute (distinct from `product_lines`,
  // which is only the values already present, used by the filter fly-out).
  product_line_options?: PricingOption[]
  product_base_options?: PricingOption[]
  // Product bases grouped by product template, so the modal's PRODUCT BASE
  // dropdown can narrow to the chosen PRODUCT TEMPLATE (cascading SELECT).
  product_bases_by_line?: Record<string, PricingOption[]>
  uom_options?: PricingOption[]
}

// ── Filter facets (Filters fly-out: product line + effective date) ─────

export function useMaterialPricingFacets() {
  const enabled = useEntityRegistered(MATERIAL_PRICING_ENTITY)
  return useQuery<PricingFacets>({
    queryKey: ['material-pricing-facets'],
    queryFn: () =>
      api.get('/claims/pricing/material-rates/facets/').then((r) => r.data),
    enabled,
  })
}

export function useLaborRateFacets() {
  const enabled = useEntityRegistered(LABOR_RATE_ENTITY)
  return useQuery<PricingFacets>({
    queryKey: ['labor-rate-facets'],
    queryFn: () =>
      api.get('/claims/pricing/labor-rates/facets/').then((r) => r.data),
    enabled,
  })
}

// ── Material pricing rates ─────────────────────────────────────────────

export function useMaterialPricingRates(params?: Record<string, string | number>) {
  const enabled = useEntityRegistered(MATERIAL_PRICING_ENTITY)
  return useQuery<PaginatedResponse<MaterialPricingRate>>({
    queryKey: ['material-pricing-rates', params],
    queryFn: () =>
      api.get('/claims/pricing/material-rates/', { params }).then((r) => r.data),
    enabled,
  })
}

export function useCreateMaterialPricingRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: MaterialPricingRateInput) =>
      api.post('/claims/pricing/material-rates/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-pricing-rates'] })
      qc.invalidateQueries({ queryKey: ['material-pricing-facets'] })
    },
  })
}

export function useUpdateMaterialPricingRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      data,
    }: {
      uuid: string
      data: Partial<MaterialPricingRateInput>
    }) =>
      api
        .put(`/claims/pricing/material-rates/${uuid}/`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-pricing-rates'] })
      qc.invalidateQueries({ queryKey: ['material-pricing-facets'] })
    },
  })
}

export function useDeleteMaterialPricingRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/claims/pricing/material-rates/${uuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-pricing-rates'] })
      qc.invalidateQueries({ queryKey: ['material-pricing-facets'] })
    },
  })
}

export function useBulkSetMaterialPrice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { uuids: string[]; price: string }) =>
      api
        .post('/claims/pricing/material-rates/bulk-set-price/', data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['material-pricing-rates'] })
      qc.invalidateQueries({ queryKey: ['material-pricing-facets'] })
    },
  })
}

// ── Labor rates ────────────────────────────────────────────────────────

export function useLaborRates(params?: Record<string, string | number>) {
  const enabled = useEntityRegistered(LABOR_RATE_ENTITY)
  return useQuery<PaginatedResponse<LaborRate>>({
    queryKey: ['labor-rates', params],
    queryFn: () =>
      api.get('/claims/pricing/labor-rates/', { params }).then((r) => r.data),
    enabled,
  })
}

export function useCreateLaborRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: LaborRateInput) =>
      api.post('/claims/pricing/labor-rates/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labor-rates'] })
      qc.invalidateQueries({ queryKey: ['labor-rate-facets'] })
    },
  })
}

export function useUpdateLaborRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Partial<LaborRateInput> }) =>
      api.put(`/claims/pricing/labor-rates/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labor-rates'] })
      qc.invalidateQueries({ queryKey: ['labor-rate-facets'] })
    },
  })
}

export function useDeleteLaborRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/claims/pricing/labor-rates/${uuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labor-rates'] })
      qc.invalidateQueries({ queryKey: ['labor-rate-facets'] })
    },
  })
}

export function useBulkSetLaborRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { uuids: string[]; price: string; currency?: string }) =>
      api
        .post('/claims/pricing/labor-rates/bulk-set-rate/', data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labor-rates'] })
      qc.invalidateQueries({ queryKey: ['labor-rate-facets'] })
    },
  })
}
